import { useEffect } from 'react';

import { CommanderActionTypesEnum } from '@spazious/dollhouse-commander';
import { FNodeTypesEnum } from '@spazious/dollhouse-fsetup';
import {
  create as createSelector,
  getIndex as getSelectorIndex,
} from '@spazious/dollhouse-selector';
import {
  createAddNodeAction,
  createNodeAction,
  createRemoveNodeAction,
  createUpdateNodeAction,
  FShallowNode,
  FShallowNodeTypesEnum,
  getShallowNodeValue,
  GraphAction,
  GraphActionType,
  GraphAddAction,
  GraphRemoveAction,
  GraphUpdateAction,
} from '@spazious/dollhouse-shallow-graph';
import { StagingArea } from '@spazious/dollhouse-staging-area';
import { useForceUpdate } from '@spazious/react-hooks';
import { useEventCallback } from '@spazious/react-hooks';

import { useGraph } from './useGraph.js';
import { useStore } from './useStore.js';

export type InputMapFn<TItem, TElement> = (event: React.ChangeEvent<TElement>) => TItem | TItem[];

export type DragState = {
  first: boolean;
  last: boolean;
};

export type DragMapFn<TItem, TState> = (state: TState) => TItem | TItem[];

export type EmptyMapFn<TItem> = () => TItem | TItem[];

export interface InputEvents<TElement> {
  onChange: (evt: React.ChangeEvent<TElement>) => void;
  onFocus: (evt: React.FocusEvent<TElement>) => void;
  onBlur: (evt: React.FocusEvent<TElement>) => void;
}

export interface DragEvents<TState> {
  onDrag: (state: TState) => void;
}

export interface ButtonEvents {
  onClick: () => void;
}

export interface BindEvents<TItem, TMeta> {
  <TElement>(type: 'input', meta: TMeta, mapFn: InputMapFn<TItem, TElement>): InputEvents<TElement>;
  <TState>(type: 'drag', meta: TMeta, mapFn: DragMapFn<TItem, TState>): DragEvents<TState>;
  (type: 'button', meta: TMeta, mapFn: EmptyMapFn<TItem>): ButtonEvents;
}

export interface AddAction {
  (type: 'add', selector: string, node: FShallowNode, parent?: string | null): void;
  (type: 'update', ...args: Parameters<typeof createUpdateNodeAction>): void;
  (type: 'remove', ...args: Parameters<typeof createRemoveNodeAction>): void;
}

export interface CreateAction {
  (type: 'add', node: FShallowNode, parent?: string | null, index?: number): GraphAddAction;
  <TValue>(
    type: 'update',
    selector: string,
    path: string,
    value: TValue | ((staleValue: TValue) => TValue)
  ): GraphUpdateAction;
  (type: 'remove', ...args: Parameters<typeof createRemoveNodeAction>): GraphRemoveAction;
}

const bindInputEvents = <TItem, TMeta, TElement>(
  staging: StagingArea<TItem, TMeta>,
  meta: TMeta,
  mapFn: InputMapFn<TItem, TElement>
): InputEvents<TElement> => {
  return {
    onFocus: () => {
      staging.prepare(true);
    },
    onChange: (event) => {
      staging.add(mapFn(event));
    },
    onBlur: () => {
      if (staging.stack.length !== 0) {
        staging.commit(meta);
      }
    },
  };
};

const bindDragEvents = <TItem, TMeta, TState extends DragState>(
  staging: StagingArea<TItem, TMeta>,
  meta: TMeta,
  mapFn: DragMapFn<TItem, TState>
): DragEvents<TState> => {
  return {
    onDrag: (state) => {
      const { first, last } = state;
      if (first) {
        staging.prepare();
      }
      staging.add(mapFn(state));
      if (last) {
        staging.commit(meta);
      }
    },
  };
};

const bindButtonEvents = <TItem, TMeta>(
  staging: StagingArea<TItem, TMeta>,
  meta: TMeta,
  mapFn: EmptyMapFn<TItem>
): ButtonEvents => {
  return {
    onClick: () => {
      staging.prepare().add(mapFn()).commit(meta);
    },
  };
};

const bindEventMapping = {
  input: bindInputEvents,
  drag: bindDragEvents,
  button: bindButtonEvents,
};

/**
 * Hook to interact with the staging stack.
 *
 * Containes a closed loop controller to create the shallow nodes
 * base on the generated graph
 *
 * Create Node -> Check current state of the graph -> Assing selector
 */
export const useStaging = () => {
  const { find, graph } = useGraph();
  const staging = useStore(({ staging }) => staging);

  const forceUpdate = useForceUpdate();

  const bind = useEventCallback((...args: any[]) => {
    const [type, meta, mapFn] = args;
    return (bindEventMapping as any)[type](staging, meta, mapFn);
  }) as BindEvents<GraphAction, CommanderActionTypesEnum>;

  const getShallow = useEventCallback((selector: string) => {
    return graph.shallow.nodes[selector] ?? null;
  });

  /**
   * Add a node to the graph with the given type and payload.
   *
   * @note You should call `commit()` after calling this function if
   * you wish apply the changes to the graph.
   */
  const add = useEventCallback((...args: Parameters<typeof createAddNodeAction>) => {
    staging.add(createAddNodeAction(...args));
  });

  /**
   * Update a node in the graph with the given uuid or selector, path, and value.
   *
   * @note You should call `commit()` after calling this function if
   * you wish apply the changes to the graph.
   */
  const update = useEventCallback((...args: Parameters<typeof createUpdateNodeAction>) => {
    staging.add(createUpdateNodeAction(...args));
  });

  /**
   * Remove a node from the graph with the given uuid or selector.
   *
   * @note You should call `commit()` after calling this function if
   * you wish apply the changes to the graph.
   */
  const remove = useEventCallback((...args: Parameters<typeof createRemoveNodeAction>) => {
    staging.add(createRemoveNodeAction(...args));
  });

  const prepare = useEventCallback((abort: boolean = false) => {
    staging.prepare(abort);
  });

  const action = useEventCallback<AddAction>((...args: any[]) => {
    // @ts-ignore
    staging.add(createNodeAction(...args));
  });

  /**
   * Commit the current staging stack to the graph.
   */
  const commit = useEventCallback((meta: CommanderActionTypesEnum) => {
    staging.commit(meta);
  });

  /**
   * Create a graph action without committing it.
   */
  const createAction = useEventCallback<CreateAction>((type: any, ...restArgs: any[]): any => {
    if (type === 'update') {
      let [selector, path, value] = restArgs;

      if (typeof value === 'function') {
        const node = getShallow(selector);
        if (!node) {
          throw new Error(`Node with selector ${selector} not found`);
        }

        value = value(getShallowNodeValue(node, path));
      }
      return createUpdateNodeAction(selector, path, value);
    } else if (type === 'add') {
      let [node, parent = null, index = 0] = restArgs as [
        FShallowNode,
        string | null | undefined,
        number
      ];
      const cursorIndex = graph.shallow.cursor ? getSelectorIndex(graph.shallow.cursor) : 0;
      const nextSelector = createSelector(cursorIndex + 1 + index, parent);
      const parentNode = find(parent);
      if (!parentNode) {
        throw new Error(`Parent node with identifier ${parent} not found`);
      }

      return createAddNodeAction(
        parent ? createSelector(parentNode.children.length + 1 + index, parent) : nextSelector,
        node
      );
    }

    // @ts-ignore
    return createNodeAction(type, ...restArgs);
  });

  useEffect(
    () =>
      staging.subscribe(() => {
        forceUpdate();
      }),
    [forceUpdate, staging]
  );

  /**
   * Automatically add a node to the staging stack if it doesn't exist in the graph
   * Taking the generated node from the graph and adding it to the staging stack as
   * override node
   */
  useEffect(() => {
    const listener = (actions: GraphAction[]) => {
      const parsed = actions
        .reduce((acc, action) => {
          if (action.type === GraphActionType.Update) {
            const { selector } = action.payload;

            // If the override node is present in the current action stack then skip the override node
            if (
              actions.find(
                (action) =>
                  action.type === GraphActionType.Add && action.payload.selector === selector
              )
            ) {
              return [...acc, action];
            }

            // If the node is not present in the shallow nodes then add it as override node
            const shallowNode = getShallow(selector);

            if (!shallowNode) {
              const foundNode = find(selector);

              if (!foundNode) {
                throw new Error(`Node with identifier ${selector} not found`);
              }

              if (
                foundNode.type === FNodeTypesEnum.Furniture ||
                foundNode.type === FNodeTypesEnum.FComposition ||
                foundNode.type === FNodeTypesEnum.Decoration
              ) {
                return [
                  ...acc,
                  createAddNodeAction(selector, {
                    type: FShallowNodeTypesEnum.FOverride,
                  }),
                  action,
                ];
              }
            }
          }

          return [...acc, action];
        }, [] as GraphAction[])
        // Sort remove actions by selector to remove the latest nodes first
        .sort((actionA, actionB) => {
          if (actionA.type === GraphActionType.Remove && actionB.type === GraphActionType.Remove) {
            return (
              getSelectorIndex(actionB.payload.selector, 0) -
              getSelectorIndex(actionA.payload.selector, 0)
            );
          }

          return 1;
        });

      // Mutate the actions array
      actions.length = 0;
      actions.push(...parsed);
    };

    staging.on('add', listener);

    return () => {
      staging.off('add', listener);
    };
  }, [find, getShallow, staging]);

  return {
    staging,
    bind,
    add,
    update,
    remove,
    action,
    commit,
    prepare,
    createAction,
    getShallow,
  };
};
